import { ApiClient } from './api';
import { StateStore } from './state';
import { StoreConfig, OrderPayload, AnalyticsConfig, BizmationConfig, AbandonedCartRecord, GallerySlide, ReviewItem } from '../types';

const store = StateStore.getInstance();

let activeGallerySlides: GallerySlide[] = [
  {
    id: 'slide_1',
    image: 'assets/images/cherry-light-pink.webp',
    title: 'Light Pink (হালকা গোলাপি)',
    subtitle: 'মিষ্টি, শান্ত ও রোমান্টিক এক ভাইব',
    color: 'light-pink',
    btnText: 'এই কালারটি নিব'
  },
  {
    id: 'slide_2',
    image: 'assets/images/cherry-deep-pink.webp',
    title: 'Deep Pink (গাঢ় গোলাপি)',
    subtitle: 'উজ্জ্বল, বোল্ড ও সবার নজর কাড়ার মতো',
    color: 'deep-pink',
    btnText: 'এই কালারটি নিব'
  },
  {
    id: 'slide_3',
    image: 'assets/images/cherry-white.webp',
    title: 'White (স্নো হোয়াইট)',
    subtitle: 'মার্জিত, স্নিগ্ধ ও রাজকীয় পরিবেশ',
    color: 'white',
    btnText: 'এই কালারটি নিব'
  },
  {
    id: 'slide_4',
    image: 'assets/images/cherry-peach.webp',
    title: 'Peach (পীচ কালার)',
    subtitle: 'আধুনিক, ওয়ার্ম ও ড্রিম ডেকোরেশন',
    color: 'peach',
    btnText: 'এই কালারটি নিব'
  },
  {
    id: 'slide_5',
    image: 'assets/images/cherry-purple.webp',
    title: 'Purple (রাউণ্ড পার্পল)',
    subtitle: 'একদম ইউনিক, আভিজাত্যপূর্ণ ও মায়াবী',
    color: 'purple',
    btnText: 'এই কালারটি নিব'
  }
];

let activeTypedHeadlines: string[] = [
  'রুমের ড্রেসিং আয়না সাজান চেরি ফুলের মিষ্টি আলোয় 🌸',
  'ইনস্টাগ্রাম ও পিন্টারেস্টের ড্রিম মিরর লুক এবার নিজের ঘরে ✨',
  'সিল্কের ঘন ফুল ও ফেয়ারি লাইট—১০০% অরিজিনাল কোয়ালিটি 💡',
  '১ টাকাও অগ্রিম দেওয়া লাগবে না—দেখে পছন্দ হলে তবেই টাকা 🎁'
];

let activeAnalyticsConfig: AnalyticsConfig | null = null;
let activeBizmationConfig: BizmationConfig | null = null;
let isOrderSubmitted = false;
let abandonedSyncTimer: any = null;

// Expose global helpers immediately so buttons work instantly
exposeGlobalHelpers();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bootstrapApp();
  });
} else {
  bootstrapApp();
}

function bootstrapApp(): void {
  // 1. Expose global helpers immediately so all onclick handlers work
  exposeGlobalHelpers();

  // 2. Initialize and render UI components IMMEDIATELY with local state (Instant Response!)
  initColorSwitcher();
  renderOrderItems();
  calculateTotals();
  updateBatteryUI();
  initCountdownTimer();
  initGalleryCarousel();
  updateWhatsAppLinks();
  initReviewTouchGestures();
  startReviewAutoSlide();
  updateReviewsCarousel();
  initTrackingListeners();
  initAbandonedCartTracker();
  initPhoneFormatter();

  // 3. Initialize Device, UUID, and IP detection
  initDeviceTracking();

  // 4. Load live microservice config asynchronously in background without blocking UI!
  loadLiveConfig().then(() => {
    initTypedJs();
    renderOrderItems();
    calculateTotals();
  }).catch(e => console.warn('Config load warning:', e));
}

/**
 * Load & apply live microservice configuration
 */
async function loadLiveConfig(): Promise<void> {
  const config: StoreConfig | null = await ApiClient.fetchConfig();
  if (!config) return;

  // 1. Sizes & Prices
  if (config.sizes) {
    Object.keys(config.sizes).forEach(key => {
      if (store.state.sizes[key]) {
        store.state.sizes[key] = { ...store.state.sizes[key], ...config.sizes[key] };
      }
    });

    if (config.sizes['12ft']) {
      const s12 = config.sizes['12ft'];
      const titleEl = document.getElementById('sizeTitle12ft');
      const badgeEl = document.getElementById('sizeBadge12ft');
      const descEl = document.getElementById('sizeDesc12ft');
      const priceEl = document.getElementById('sizePrice12ft');
      const oldPriceEl = document.getElementById('sizeOldPrice12ft');
      const optEl = document.getElementById('optSize12ft');
      if (titleEl && s12.name) titleEl.textContent = s12.name;
      if (badgeEl && s12.badge) badgeEl.textContent = s12.badge;
      if (descEl && s12.desc) descEl.textContent = s12.desc;
      if (priceEl && s12.price) priceEl.textContent = '৳' + formatBanglaNumber(s12.price);
      if (oldPriceEl && s12.oldPrice) oldPriceEl.textContent = '৳' + formatBanglaNumber(s12.oldPrice);
      if (optEl && s12.name && s12.price) optEl.textContent = `${s12.name} (৳${formatBanglaNumber(s12.price)})`;
    }

    if (config.sizes['6ft']) {
      const s6 = config.sizes['6ft'];
      const titleEl = document.getElementById('sizeTitle6ft');
      const descEl = document.getElementById('sizeDesc6ft');
      const priceEl = document.getElementById('sizePrice6ft');
      const oldPriceEl = document.getElementById('sizeOldPrice6ft');
      const optEl = document.getElementById('optSize6ft');
      if (titleEl && s6.name) titleEl.textContent = s6.name;
      if (descEl && s6.desc) descEl.textContent = s6.desc;
      if (priceEl && s6.price) priceEl.textContent = '৳' + formatBanglaNumber(s6.price);
      if (oldPriceEl && s6.oldPrice) oldPriceEl.textContent = '৳' + formatBanglaNumber(s6.oldPrice);
      if (optEl && s6.name && s6.price) optEl.textContent = `${s6.name} (৳${formatBanglaNumber(s6.price)})`;
    }
  }

  // 2. Store Info & Contact
  if (config.store) {
    const s = config.store;
    if (s.whatsapp) {
      store.state.whatsappNumber = s.whatsapp;
    }
    const phone = s.phone || s.whatsapp;
    if (phone) {
      const phoneSpan = document.getElementById('headerPhoneText');
      if (phoneSpan) phoneSpan.textContent = phone;
      const callBtn = document.getElementById('headerCallBtn') as HTMLAnchorElement | null;
      if (callBtn) callBtn.href = 'tel:' + phone;
      const footPhoneLink = document.getElementById('footerPhoneLink') as HTMLAnchorElement | null;
      if (footPhoneLink) {
        footPhoneLink.href = 'tel:' + phone;
        footPhoneLink.textContent = phone;
      }
    }
    if (s.brandName) {
      const hEn = document.getElementById('headerBrandEn');
      const fEn = document.getElementById('footerBrandEn');
      if (hEn) hEn.textContent = s.brandName;
      if (fEn) fEn.textContent = s.brandName;
    }
    if (s.brandNameBn) {
      const hBn = document.getElementById('headerBrandBn');
      const fBn = document.getElementById('footerBrandBn');
      if (hBn) hBn.textContent = s.brandNameBn;
      if (fBn) fBn.textContent = s.brandNameBn;
    }
    if (s.facebookPage) {
      const hFb = document.getElementById('headerFbLink') as HTMLAnchorElement | null;
      const fFb = document.getElementById('footerFbLink') as HTMLAnchorElement | null;
      if (hFb) hFb.href = s.facebookPage;
      if (fFb) fFb.href = s.facebookPage;
    }
    if (s.productName) {
      const prodNameEl = document.getElementById('summaryProductName');
      if (prodNameEl) prodNameEl.textContent = s.productName;
    }
    if (s.footerDesc) {
      const footDescEl = document.getElementById('footerDescText');
      if (footDescEl) {
        footDescEl.innerHTML = `${s.footerDesc} <a href="tel:${phone}" id="footerPhoneLink" style="color: #ff85a1; text-decoration: none; font-weight: 700;">${phone}</a>`;
      }
    }
  }

  // 3. Hero Section
  if (config.hero) {
    const h = config.hero;
    if (h.offerTag) {
      const tagEl = document.getElementById('heroOfferTag');
      if (tagEl) tagEl.textContent = h.offerTag;
    }
    if (h.title) {
      const titleEl = document.getElementById('heroTitle');
      if (titleEl) titleEl.innerHTML = h.title;
    }
    if (h.showcaseBadge) {
      const badgeEl = document.getElementById('showcaseBadgeText');
      if (badgeEl) badgeEl.textContent = h.showcaseBadge;
    }
    if (h.typedHeadlines && h.typedHeadlines.length > 0) {
      activeTypedHeadlines = h.typedHeadlines;
    }
  }

  // 4. Feature Highlights
  if (config.features && config.features.length > 0) {
    config.features.forEach((feat, i) => {
      const tEl = document.getElementById('featTitle' + i);
      const dEl = document.getElementById('featDesc' + i);
      if (tEl && feat.title) tEl.textContent = feat.title;
      if (dEl && feat.subtitle) dEl.textContent = feat.subtitle;
    });
  }

  // 5. Specs Table
  if (config.specs) {
    const sp = config.specs;
    const spProd = document.getElementById('specProduct');
    const spSizes = document.getElementById('specSizes');
    const spColors = document.getElementById('specColors');
    const spFlower = document.getElementById('specFlower');
    const spDel = document.getElementById('specDelivery');
    if (spProd && sp.product) spProd.textContent = sp.product;
    if (spSizes && sp.sizes) spSizes.textContent = sp.sizes;
    if (spColors && sp.colors) spColors.textContent = sp.colors;
    if (spFlower && sp.flowerType) spFlower.textContent = sp.flowerType;
    if (spDel && sp.delivery) spDel.textContent = sp.delivery;
  }

  // 6. Reviews
  if (config.reviews) {
    const r = config.reviews;
    if (r.summary) {
      const sumEl = document.getElementById('reviewSummary');
      if (sumEl) sumEl.textContent = r.summary;
    }
    await initReviews(r.items);
  } else {
    await initReviews();
  }

  // 7. FAQs
  if (config.faqs && Array.isArray(config.faqs) && config.faqs.length > 0) {
    renderFaqs(config.faqs);
    config.faqs.forEach((faq, i) => {
      const qEl = document.getElementById('faqQ' + i);
      const aEl = document.getElementById('faqA' + i);
      if (qEl && faq.q) qEl.textContent = faq.q;
      if (aEl && faq.a) aEl.textContent = faq.a;
    });
  }

  // 8. Variants & Colors
  if (config.variants) {
    store.state.variants = { ...store.state.variants, ...config.variants };
  }
  if (config.colors) {
    store.state.colors = { ...store.state.colors, ...config.colors };
  }

  // 9. Delivery
  if (config.delivery) {
    store.state.deliveryRates = {
      insideDhaka: config.delivery.insideDhaka,
      outsideDhaka: config.delivery.outsideDhaka
    };
    const inEl = document.getElementById('delChargeInsideText');
    const outEl = document.getElementById('delChargeOutsideText');
    if (inEl) inEl.textContent = '৳' + formatBanglaNumber(config.delivery.insideDhaka);
    if (outEl) outEl.textContent = '৳' + formatBanglaNumber(config.delivery.outsideDhaka);

    store.state.deliveryCharge = (store.state.deliveryCharge === 70 || store.state.deliveryCharge === config.delivery.insideDhaka)
      ? config.delivery.insideDhaka
      : config.delivery.outsideDhaka;
  }

  // 10. Security (Blocked IPs)
  if (config.security?.blockedIps) {
    store.state.blockedIps = config.security.blockedIps;
    checkIpBlockStatus();
  }

  // 11. Analytics (Meta Pixel, GA4, GTM, Search Console, Domain Verification)
  if (config.analytics) {
    activeAnalyticsConfig = config.analytics;
    initAnalytics(config.analytics);
  }

  // 12. Bizmation API Config
  if (config.bizmation) {
    activeBizmationConfig = config.bizmation;
  }

  // 13. Gallery Carousel Slides
  if (config.gallery && Array.isArray(config.gallery) && config.gallery.length > 0) {
    activeGallerySlides = config.gallery;
    renderGalleryCarousel(activeGallerySlides);
  }

  selectSize(store.state.selectedSize);
  updateWhatsAppLinks();
}

/**
 * Typed.js headline
 */
function initTypedJs(): void {
  if (typeof window.Typed !== 'undefined') {
    new window.Typed('#typedHeadline', {
      strings: activeTypedHeadlines,
      typeSpeed: 40,
      backSpeed: 25,
      backDelay: 2200,
      loop: true,
      showCursor: true,
      cursorChar: '|'
    });
  }
}

/**
 * Dynamic Image Updater (Size + Color matrix)
 */
function updateImages(): void {
  const imgUrl = store.currentImage;

  const mainImg = document.getElementById('mainShowcaseImg') as HTMLImageElement | null;
  if (mainImg) {
    mainImg.classList.add('fading');
    setTimeout(() => {
      mainImg.src = imgUrl;
      const pictureSource = mainImg.parentElement?.querySelector('source');
      if (pictureSource) {
        pictureSource.srcset = imgUrl;
        if (!imgUrl.toLowerCase().endsWith('.webp')) {
          pictureSource.removeAttribute('type');
        } else {
          pictureSource.setAttribute('type', 'image/webp');
        }
      }
      mainImg.classList.remove('fading');
    }, 150);
  }

  const summaryImg = document.getElementById('summaryImg') as HTMLImageElement | null;
  if (summaryImg) {
    summaryImg.src = imgUrl;
  }
}

/**
 * Size Switcher
 */
/**
 * Size Switcher
 */
export function selectSize(sizeKey: string): void {
  if (!store.state.sizes[sizeKey]) return;
  store.state.selectedSize = sizeKey;

  const cards = document.querySelectorAll('.size-choice-card');
  cards.forEach(card => {
    if (card.getAttribute('onclick')?.includes(sizeKey)) {
      card.classList.add('active');
    } else {
      card.classList.remove('active');
    }
  });

  const sizeBtns = document.querySelectorAll('.size-btn');
  sizeBtns.forEach(btn => {
    if (btn.getAttribute('data-size') === sizeKey) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });

  const curSize = store.currentSize;

  const heroPrice = document.getElementById('heroPrice');
  const heroOldPrice = document.getElementById('heroOldPrice');
  const heroSavings = document.getElementById('heroSavings');
  const stickyPriceText = document.getElementById('stickyPriceText');

  if (heroPrice) heroPrice.textContent = '৳' + formatBanglaNumber(curSize.price);
  if (heroOldPrice) heroOldPrice.textContent = '৳' + formatBanglaNumber(curSize.oldPrice);
  if (heroSavings) heroSavings.textContent = 'সাশ্রয় ৳' + formatBanglaNumber(curSize.savings) + '!';
  if (stickyPriceText) stickyPriceText.textContent = '৳' + formatBanglaNumber(curSize.price);

  // Sync with first item in multi-item cart
  if (store.state.cartItems && store.state.cartItems.length > 0) {
    store.state.cartItems[0].size = sizeKey;
    store.state.cartItems[0].price = curSize.price;
    renderOrderItems();
  }

  updateImages();
  calculateTotals();
  updateWhatsAppLinks();
  scheduleAbandonedCartSync();
}

/**
 * Color Switcher
 */
function initColorSwitcher(): void {
  const colorBtns = document.querySelectorAll<HTMLButtonElement>('.color-btn');
  colorBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const colorKey = btn.getAttribute('data-color');
      if (colorKey) setColor(colorKey);
    });
  });
}

export function setColor(colorKey: string): void {
  const colorInfo = store.state.colors[colorKey];
  if (!colorInfo) return;

  store.state.selectedColor = colorKey;

  document.querySelectorAll('.color-btn').forEach(btn => {
    if (btn.getAttribute('data-color') === colorKey) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });

  const colorLabel = document.getElementById('currentColorLabel');
  if (colorLabel) colorLabel.textContent = colorInfo.name;

  // Sync with first item in multi-item cart
  if (store.state.cartItems && store.state.cartItems.length > 0) {
    store.state.cartItems[0].color = colorKey;
    renderOrderItems();
  }

  updateImages();
  updateWhatsAppLinks();
  scheduleAbandonedCartSync();
}

/**
 * Quantity Controller (Legacy Fallback)
 */
export function changeQuantity(change: number): void {
  if (store.state.cartItems && store.state.cartItems.length > 0) {
    updateOrderItem(0, 'qty_delta', change);
  }
}

/**
 * Delivery Area Selection
 */
export function updateDeliveryArea(area: 'inside' | 'outside'): void {
  const deliveryRadios = document.querySelectorAll('.delivery-radio');
  deliveryRadios.forEach(label => {
    const radio = label.querySelector<HTMLInputElement>('input[type="radio"]');
    if (radio && radio.value === area) {
      radio.checked = true;
      label.classList.add('active');
    } else {
      label.classList.remove('active');
    }
  });

  store.state.deliveryCharge = (area === 'inside') 
    ? store.state.deliveryRates.insideDhaka 
    : store.state.deliveryRates.outsideDhaka;
  calculateTotals();
}

/**
 * Multi-Item Order Cart Controller
 */
export function renderOrderItems(): void {
  const container = document.getElementById('orderItemsList');
  if (!container) return;

  container.innerHTML = store.state.cartItems.map((item, index) => {
    const isOnlyOne = store.state.cartItems.length <= 1;
    const itemSubtotal = item.price * item.quantity;
    const sizeName12 = store.state.sizes['12ft']?.name || '১২ ফিট (১৪৪ ইঞ্চি)';
    const price12 = store.state.sizes['12ft']?.price || 690;
    const sizeName6 = store.state.sizes['6ft']?.name || '৬ ফিট (৭২ ইঞ্চি)';
    const price6 = store.state.sizes['6ft']?.price || 390;

    const itemImg = store.state.variants[item.size]?.[item.color] 
      || store.state.colors[item.color]?.image 
      || 'assets/images/cherry-light-pink.webp';
    const colorInfo = store.state.colors[item.color];
    const colorName = colorInfo?.name || item.color;
    const sizeName = item.size === '6ft' ? '৬ ফিট' : '১২ ফিট';

    return `
      <div class="cart-item-row" data-index="${index}">
        <div class="item-row-header">
          <div class="item-title-tag">
            <img src="${itemImg}" alt="${sizeName} ${colorName}" class="item-mini-thumb" onerror="this.src='assets/images/cherry-light-pink.webp'">
            <div>
              <span class="item-badge-text"><i class="fa-solid fa-bag-shopping" style="color: #5a189a;"></i> আইটেম #${index + 1}</span>
              <span class="item-summary-pill">${sizeName} • ${colorName}</span>
            </div>
          </div>
          ${!isOnlyOne ? `
            <button type="button" class="btn-remove-item" onclick="removeOrderItem(${index})" title="আইটেমটি মুছুন">
              <i class="fa-solid fa-trash-can"></i> মুছুন
            </button>
          ` : ''}
        </div>

        <div class="item-controls-grid">
          <!-- Size Selector -->
          <div class="form-group" style="margin-bottom:0;">
            <label style="font-size:12px; margin-bottom:4px; color:var(--text-dim);">সাইজ:</label>
            <select class="form-select" onchange="updateOrderItem(${index}, 'size', this.value)" style="padding: 8px 10px; font-size: 13px;">
              <option value="6ft" ${item.size === '6ft' ? 'selected' : ''}>৬ ফিট (৳${formatBanglaNumber(price6)})</option>
              <option value="12ft" ${item.size === '12ft' ? 'selected' : ''}>১২ ফিট (৳${formatBanglaNumber(price12)})</option>
            </select>
          </div>

          <!-- Color Selector -->
          <div class="form-group" style="margin-bottom:0;">
            <label style="font-size:12px; margin-bottom:4px; color:var(--text-dim);">কালার:</label>
            <select class="form-select" onchange="updateOrderItem(${index}, 'color', this.value)" style="padding: 8px 10px; font-size: 13px;">
              <option value="light-pink" ${item.color === 'light-pink' ? 'selected' : ''}>🌸 Light Pink (হালকা গোলাপি)</option>
              <option value="deep-pink" ${item.color === 'deep-pink' ? 'selected' : ''}>🌺 Deep Pink (গাঢ় গোলাপি)</option>
              <option value="white" ${item.color === 'white' ? 'selected' : ''}>🤍 White (স্নো হোয়াইট)</option>
              <option value="peach" ${item.color === 'peach' ? 'selected' : ''}>🍑 Peach (পীচ কালার)</option>
              <option value="purple" ${item.color === 'purple' ? 'selected' : ''}>💜 Purple (পার্পল)</option>
            </select>
          </div>

          <!-- Quantity Controller -->
          <div class="form-group" style="margin-bottom:0;">
            <label style="font-size:12px; margin-bottom:4px; color:var(--text-dim);">পরিমাণ:</label>
            <div class="quantity-controller">
              <button type="button" class="qty-btn minus" style="width:28px; height:28px;" onclick="updateOrderItem(${index}, 'qty_delta', -1)" aria-label="পরিমাণ কমান"><i class="fa-solid fa-minus" style="font-size:10px;"></i></button>
              <span style="width:30px; text-align:center; font-weight:700; font-family:var(--font-en); font-size:14px; color:#1e1b2e;">${item.quantity}</span>
              <button type="button" class="qty-btn plus" style="width:28px; height:28px;" onclick="updateOrderItem(${index}, 'qty_delta', 1)" aria-label="পরিমাণ বাড়ান"><i class="fa-solid fa-plus" style="font-size:10px;"></i></button>
            </div>
          </div>

          <!-- Subtotal Price -->
          <div style="text-align: right; display:flex; flex-direction:column; justify-content:center;">
            <span style="font-size:11px; color:var(--text-dim);">দাম:</span>
            <span class="item-subtotal-price">৳${formatBanglaNumber(itemSubtotal)}</span>
          </div>
        </div>
      </div>
    `;
  }).join('');
}

export function addOrderItem(preferredSize?: string, preferredColor?: string): void {
  const availableColors = ['light-pink', 'deep-pink', 'white', 'peach', 'purple'];
  const nextColor = preferredColor || availableColors[store.state.cartItems.length % availableColors.length];
  const nextSize = preferredSize || (store.state.cartItems.length % 2 === 1 ? '6ft' : '12ft');
  const price = store.state.sizes[nextSize]?.price || (nextSize === '6ft' ? 390 : 690);

  store.state.cartItems.push({
    id: 'item-' + Date.now() + '-' + Math.random().toString(36).substring(2, 6),
    size: nextSize,
    color: nextColor,
    quantity: 1,
    price: price
  });

  renderOrderItems();
  calculateTotals();
  updateWhatsAppLinks();
  scheduleAbandonedCartSync();
}

export function applyCombo(comboType: string): void {
  const p6 = store.state.sizes['6ft']?.price || 390;
  const p12 = store.state.sizes['12ft']?.price || 690;

  if (comboType === '6ft_12ft') {
    // 1 piece 6ft Pink + 1 piece 12ft Purple
    store.state.cartItems = [
      { id: 'item-' + Date.now() + '-1', size: '6ft', color: 'light-pink', quantity: 1, price: p6 },
      { id: 'item-' + Date.now() + '-2', size: '12ft', color: 'purple', quantity: 1, price: p12 }
    ];
  } else if (comboType === 'all_5') {
    // All 5 colors complete set
    const allColors = ['light-pink', 'deep-pink', 'white', 'peach', 'purple'];
    store.state.cartItems = allColors.map((col, idx) => ({
      id: 'item-' + Date.now() + '-' + idx,
      size: '12ft',
      color: col,
      quantity: 1,
      price: p12
    }));
  } else if (comboType === '3_popular') {
    // 3 popular colors (Light Pink, Deep Pink, Purple)
    store.state.cartItems = [
      { id: 'item-' + Date.now() + '-1', size: '12ft', color: 'light-pink', quantity: 1, price: p12 },
      { id: 'item-' + Date.now() + '-2', size: '12ft', color: 'deep-pink', quantity: 1, price: p12 },
      { id: 'item-' + Date.now() + '-3', size: '12ft', color: 'purple', quantity: 1, price: p12 }
    ];
  }

  renderOrderItems();
  calculateTotals();
  updateWhatsAppLinks();
  scheduleAbandonedCartSync();
}

export function removeOrderItem(index: number): void {
  if (store.state.cartItems.length <= 1) return;
  store.state.cartItems.splice(index, 1);
  renderOrderItems();
  calculateTotals();
  updateWhatsAppLinks();
  scheduleAbandonedCartSync();
}

export function updateOrderItem(index: number, field: string, value: any): void {
  const item = store.state.cartItems[index];
  if (!item) return;

  if (field === 'size') {
    item.size = value;
    item.price = store.state.sizes[value]?.price || (value === '6ft' ? 390 : 690);
    if (index === 0) {
      store.state.selectedSize = value;
      updateImages();
    }
  } else if (field === 'color') {
    item.color = value;
    if (index === 0) {
      store.state.selectedColor = value;
      updateImages();
    }
  } else if (field === 'qty_delta') {
    const newQty = item.quantity + value;
    if (newQty >= 1 && newQty <= 10) {
      item.quantity = newQty;
    }
  }

  renderOrderItems();
  calculateTotals();
  updateWhatsAppLinks();
  scheduleAbandonedCartSync();
}

export function toggleBatteryAddon(checked: boolean): void {
  store.state.hasBattery = checked;
  const chk = document.getElementById('batteryAddonCheck') as HTMLInputElement | null;
  if (chk) chk.checked = checked;

  const card = document.getElementById('batteryAddonCard');
  if (card) {
    if (checked) card.classList.add('selected');
    else card.classList.remove('selected');
  }

  const qtyBox = document.getElementById('batteryQtyBox');
  if (qtyBox) {
    qtyBox.style.display = checked ? 'block' : 'none';
  }

  updateBatteryUI();
  calculateTotals();
  updateWhatsAppLinks();
  scheduleAbandonedCartSync();
}

export function changeBatteryQuantity(delta: number): void {
  const current = store.state.batteryQuantity || 1;
  const newQty = Math.max(1, Math.min(10, current + delta));
  setBatteryQuantity(newQty);
}

export function setBatteryQuantity(qty: number): void {
  const safeQty = Math.max(1, Math.min(10, Math.floor(qty)));
  store.state.batteryQuantity = safeQty;

  // Auto-check if currently unchecked
  if (!store.state.hasBattery) {
    store.state.hasBattery = true;
    const chk = document.getElementById('batteryAddonCheck') as HTMLInputElement | null;
    if (chk) chk.checked = true;
    const card = document.getElementById('batteryAddonCard');
    if (card) card.classList.add('selected');
    const qtyBox = document.getElementById('batteryQtyBox');
    if (qtyBox) qtyBox.style.display = 'block';
  }

  updateBatteryUI();
  calculateTotals();
  updateWhatsAppLinks();
  scheduleAbandonedCartSync();
}

export function updateBatteryUI(): void {
  const qty = store.state.batteryQuantity || 1;
  const totalBatteries = qty * 3;
  const totalPrice = store.state.batteryPrice * qty;

  const countEl = document.getElementById('batterySetCount');
  if (countEl) countEl.textContent = formatBanglaNumber(qty);

  const hintEl = document.getElementById('batteryPiecesHint');
  if (hintEl) hintEl.textContent = `(${formatBanglaNumber(qty)} সেট = ${formatBanglaNumber(totalBatteries)}টি ব্যাটারি)`;

  const priceTag = document.getElementById('batteryPriceTag');
  if (priceTag) priceTag.textContent = `(+৳${formatBanglaNumber(totalPrice)})`;

  // Quick selection chips active highlight
  for (let i = 1; i <= 4; i++) {
    const chip = document.getElementById(`batchip-${i}`);
    if (chip) {
      if (qty === i) chip.classList.add('active');
      else chip.classList.remove('active');
    }
  }
}

/**
 * Auto-Moving Gallery Carousel (Dynamic from config.gallery)
 */
let currentSlideIndex = 0;
let carouselAutoTimer: any = null;

export function renderGalleryCarousel(slides: GallerySlide[] = activeGallerySlides): void {
  const track = document.getElementById('galleryTrack');
  const dotsContainer = document.getElementById('carouselDots');
  if (!track) return;

  activeGallerySlides = slides;

  track.innerHTML = slides.map((item) => `
    <div class="gallery-carousel-slide">
      <div class="carousel-card" onclick="selectColorFromGallery('${item.color || ''}')">
        <div class="img-zoom-wrap">
          <picture>
            <source srcset="${item.image}" type="image/webp">
            <img src="${item.image}" alt="${item.title}" width="720" height="720" loading="lazy" decoding="async" onerror="this.src='assets/images/cherry-light-pink.webp'">
          </picture>
        </div>
        <div class="card-caption">
          <div>
            <h3>${item.title}</h3>
            ${item.subtitle ? `<p>${item.subtitle}</p>` : ''}
          </div>
          <button type="button" class="btn-outline-sm">${item.btnText || 'এই কালারটি নিব'}</button>
        </div>
      </div>
    </div>
  `).join('');

  if (dotsContainer) {
    const isMobile = window.innerWidth <= 600;
    const isTablet = window.innerWidth > 600 && window.innerWidth <= 991;
    const slidesPerView = isMobile ? 1 : (isTablet ? 2 : 3);
    const maxSteps = Math.max(1, slides.length - slidesPerView + 1);

    dotsContainer.innerHTML = Array.from({ length: maxSteps }).map((_, i) => `
      <button type="button" class="carousel-dot ${i === 0 ? 'active' : ''}" onclick="carouselGoTo(${i})" aria-label="ছবি ${i + 1}"></button>
    `).join('');
  }

  currentSlideIndex = 0;
  updateCarouselView(false);
}

function updateCarouselView(animate = true): void {
  const track = document.getElementById('galleryTrack');
  if (!track) return;

  track.style.transition = animate ? 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)' : 'none';

  const isMobile = window.innerWidth <= 600;
  const isTablet = window.innerWidth > 600 && window.innerWidth <= 991;
  const percentPerSlide = isMobile ? 100 : (isTablet ? 50 : 33.333);
  const slidesPerView = isMobile ? 1 : (isTablet ? 2 : 3);
  const totalSlides = activeGallerySlides.length;
  const maxSteps = Math.max(1, totalSlides - slidesPerView + 1);

  if (currentSlideIndex >= maxSteps) {
    currentSlideIndex = 0;
  }

  track.style.transform = `translateX(-${currentSlideIndex * percentPerSlide}%)`;

  const dots = document.querySelectorAll('.carousel-dot');
  dots.forEach((dot, i) => {
    if (i === currentSlideIndex) {
      dot.classList.add('active');
    } else {
      dot.classList.remove('active');
    }
  });
}

function initGalleryCarousel(): void {
  const track = document.getElementById('galleryTrack');
  const carouselWrapper = document.getElementById('galleryCarousel');
  if (!track || !carouselWrapper) return;

  window.carouselNext = () => {
    const isMobile = window.innerWidth <= 600;
    const isTablet = window.innerWidth > 600 && window.innerWidth <= 991;
    const slidesPerView = isMobile ? 1 : (isTablet ? 2 : 3);
    const maxSteps = Math.max(1, activeGallerySlides.length - slidesPerView + 1);
    currentSlideIndex = (currentSlideIndex + 1) % maxSteps;
    updateCarouselView(true);
  };

  window.carouselPrev = () => {
    const isMobile = window.innerWidth <= 600;
    const isTablet = window.innerWidth > 600 && window.innerWidth <= 991;
    const slidesPerView = isMobile ? 1 : (isTablet ? 2 : 3);
    const maxSteps = Math.max(1, activeGallerySlides.length - slidesPerView + 1);
    currentSlideIndex = (currentSlideIndex - 1 + maxSteps) % maxSteps;
    updateCarouselView(true);
  };

  window.carouselGoTo = (i: number) => {
    const isMobile = window.innerWidth <= 600;
    const isTablet = window.innerWidth > 600 && window.innerWidth <= 991;
    const slidesPerView = isMobile ? 1 : (isTablet ? 2 : 3);
    const maxSteps = Math.max(1, activeGallerySlides.length - slidesPerView + 1);
    currentSlideIndex = i % maxSteps;
    updateCarouselView(true);
  };

  function startAutoSlide(): void {
    if (carouselAutoTimer) clearInterval(carouselAutoTimer);
    carouselAutoTimer = setInterval(() => {
      window.carouselNext?.();
    }, 2800);
  }

  function stopAutoSlide(): void {
    if (carouselAutoTimer) {
      clearInterval(carouselAutoTimer);
      carouselAutoTimer = null;
    }
  }

  carouselWrapper.addEventListener('mouseenter', stopAutoSlide);
  carouselWrapper.addEventListener('mouseleave', startAutoSlide);
  carouselWrapper.addEventListener('touchstart', stopAutoSlide, { passive: true });
  carouselWrapper.addEventListener('touchend', startAutoSlide, { passive: true });

  let touchStartX = 0;
  carouselWrapper.addEventListener('touchstart', (e: TouchEvent) => {
    touchStartX = e.changedTouches[0].screenX;
  }, { passive: true });

  carouselWrapper.addEventListener('touchend', (e: TouchEvent) => {
    const touchEndX = e.changedTouches[0].screenX;
    const diff = touchEndX - touchStartX;
    if (diff > 45) {
      window.carouselPrev?.();
    } else if (diff < -45) {
      window.carouselNext?.();
    }
  }, { passive: true });

  window.addEventListener('resize', () => {
    updateCarouselView(false);
    updateReviewsCarousel();
  });

  startAutoSlide();
  updateCarouselView(false);
}

/**
 * Dynamic Calculation with Multi-Item Cart & Battery Addon
 */
function calculateTotals(): void {
  const subtotal = store.subtotal;
  const grandTotal = store.grandTotal;

  const calcSubtotal = document.getElementById('calcSubtotal');
  const calcDelivery = document.getElementById('calcDelivery');
  const calcGrandTotal = document.getElementById('calcGrandTotal');
  const btnTotalText = document.getElementById('btnTotalText');
  const calcBatteryRow = document.getElementById('calcBatteryRow');
  const calcBatteryPrice = document.getElementById('calcBatteryPrice');

  if (calcSubtotal) calcSubtotal.textContent = '৳' + formatBanglaNumber(subtotal);
  if (calcDelivery) calcDelivery.textContent = '৳' + formatBanglaNumber(store.state.deliveryCharge);
  if (calcGrandTotal) calcGrandTotal.textContent = '৳' + formatBanglaNumber(grandTotal);
  if (btnTotalText) btnTotalText.textContent = '৳' + formatBanglaNumber(grandTotal);

  if (calcBatteryRow) {
    calcBatteryRow.style.display = store.state.hasBattery ? 'flex' : 'none';
  }
  const calcBatteryLabel = document.getElementById('calcBatteryLabel');
  if (calcBatteryLabel) {
    const batQty = store.state.batteryQuantity || 1;
    calcBatteryLabel.textContent = `পেন্সিল ব্যাটারি (${formatBanglaNumber(batQty)} সেট - ${formatBanglaNumber(batQty * 3)}টি):`;
  }
  if (calcBatteryPrice) {
    calcBatteryPrice.textContent = '+৳' + formatBanglaNumber(store.batteryTotal);
  }
  const stickyPriceText = document.getElementById('stickyPriceText');
  if (stickyPriceText) stickyPriceText.textContent = '৳' + formatBanglaNumber(grandTotal);
}

function formatBanglaNumber(num: number): string {
  const bnDigits = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return num.toString().split('').map(char => {
    const idx = parseInt(char, 10);
    return !isNaN(idx) ? bnDigits[idx] : char;
  }).join('');
}

/**
 * Countdown Timer
 */
function initCountdownTimer(): void {
  let totalSeconds = (4 * 3600) + (48 * 60) + 22;

  const hoursEl = document.getElementById('hours');
  const minutesEl = document.getElementById('minutes');
  const secondsEl = document.getElementById('seconds');

  setInterval(() => {
    if (totalSeconds <= 0) {
      totalSeconds = 6 * 3600;
    } else {
      totalSeconds--;
    }

    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    if (hoursEl) hoursEl.textContent = h.toString().padStart(2, '0');
    if (minutesEl) minutesEl.textContent = m.toString().padStart(2, '0');
    if (secondsEl) secondsEl.textContent = s.toString().padStart(2, '0');
  }, 1000);
}

/**
 * WhatsApp Link Formatter with Multi-Item & Battery Breakdown
 */
function updateWhatsAppLinks(): void {
  const items = store.state.cartItems;
  const itemsText = items.map((it, idx) => {
    const sizeName = store.state.sizes[it.size]?.name || it.size;
    const colorName = store.state.colors[it.color]?.name || it.color;
    return `${idx + 1}. ${sizeName} (${colorName}) - ${it.quantity} পিস`;
  }).join('\n');

  const batQty = store.state.batteryQuantity || 1;
  const batteryText = store.state.hasBattery 
    ? `\n- ব্যাটারি: ${formatBanglaNumber(batQty)} সেট (${formatBanglaNumber(batQty * 3)}টি AA পেন্সিল ব্যাটারি) (+৳${formatBanglaNumber(store.batteryTotal)})` 
    : '';
  const total = store.grandTotal;
  const waNum = store.state.whatsappNumber.replace(/[^0-9]/g, '');

  const msg = `হ্যালো Sazook,\nআমি Artificial Cherry Flower String light নিতে চাচ্ছি।\n\nঅর্ডার আইটেম:\n${itemsText}${batteryText}\n- মোট প্রদেয় টাকা: ৳${formatBanglaNumber(total)} (ক্যাশ অন ডেলিভারিতে পাঠাবেন)।`;
  const encoded = encodeURIComponent(msg);

  const heroWa = document.getElementById('heroWhatsappBtn') as HTMLAnchorElement | null;
  const formWa = document.getElementById('formWhatsappBtn') as HTMLAnchorElement | null;
  const stickyWa = document.querySelector<HTMLAnchorElement>('.sticky-btn-wa');

  const waUrl = `https://wa.me/88${waNum}?text=${encoded}`;

  if (heroWa) heroWa.href = waUrl;
  if (formWa) formWa.href = waUrl;
  if (stickyWa) stickyWa.href = waUrl;
}

/**
 * Smart Phone Sanitizer & Normalizer
 * Converts Bengali digits, strips +, -, spaces, 880 prefix and formats to clean 11 digits
 */
export function sanitizePhoneNumber(phone: string): string {
  if (!phone) return '';
  const bnDigits = ['০','১','২','৩','৪','৫','৬','৭','৮','৯'];
  let clean = phone.trim();
  bnDigits.forEach((d, i) => {
    clean = clean.split(d).join(String(i));
  });
  clean = clean.replace(/[^0-9]/g, '');
  if (clean.startsWith('880')) {
    clean = clean.slice(2);
  }
  if (clean.length === 10 && clean.startsWith('1')) {
    clean = '0' + clean;
  }
  return clean;
}

/**
 * Real-time Phone Formatter & Listener for #custPhone
 */
function initPhoneFormatter(): void {
  const phoneInput = document.getElementById('custPhone') as HTMLInputElement | null;
  if (!phoneInput) return;

  const cleanAndFormat = () => {
    const raw = phoneInput.value;
    if (!raw) return;
    const cleaned = sanitizePhoneNumber(raw);
    if (cleaned && cleaned !== raw && cleaned.length <= 11) {
      phoneInput.value = cleaned;
    }
  };

  phoneInput.addEventListener('input', cleanAndFormat);
  phoneInput.addEventListener('change', cleanAndFormat);
  phoneInput.addEventListener('blur', cleanAndFormat);
  phoneInput.addEventListener('paste', () => setTimeout(cleanAndFormat, 10));
}

/**
 * Form Submit Handler - Resilient and Isolated against any Tracking/Network Glitches
 */
export async function handleFormSubmit(e: Event): Promise<void> {
  e.preventDefault();

  const nameInput = document.getElementById('custName') as HTMLInputElement | null;
  const phoneInput = document.getElementById('custPhone') as HTMLInputElement | null;
  const emailInput = document.getElementById('custEmail') as HTMLInputElement | null;
  const addressInput = document.getElementById('custAddress') as HTMLTextAreaElement | null;
  const submitBtn = document.getElementById('submitOrderBtn') as HTMLButtonElement | null;
  const originalBtnText = submitBtn?.innerHTML || '<i class="fa-solid fa-circle-check"></i> <span>অর্ডার কনফার্ম করুন</span>';

  const name = (nameInput?.value || '').trim();
  const rawPhone = (phoneInput?.value || '').trim();
  const cleanPhone = sanitizePhoneNumber(rawPhone);
  const email = (emailInput?.value || '').trim();
  const address = (addressInput?.value || '').trim();

  // Validate Name
  if (!name || name.length < 2) {
    alert('দয়া করে আপনার নাম সঠিকভাবে লিখুন।');
    nameInput?.focus();
    return;
  }

  // Validate Phone
  if (!cleanPhone || cleanPhone.length !== 11 || !cleanPhone.startsWith('01')) {
    alert('দয়া করে ১১ ডিজিটের সঠিক মোবাইল নম্বর দিন (যেমন: 017xxxxxxxx)।');
    if (phoneInput) {
      phoneInput.value = cleanPhone;
      phoneInput.focus();
    }
    return;
  }

  // Auto-fill sanitized clean phone into input field
  if (phoneInput && phoneInput.value !== cleanPhone) {
    phoneInput.value = cleanPhone;
  }

  // Validate Address
  if (!address || address.length < 5) {
    alert('দয়া করে আপনার সম্পূর্ণ ডেলিভারি ঠিকানা (বাসা নং, রোড, থানা, জেলা) বিস্তারিত লিখুন।');
    addressInput?.focus();
    return;
  }

  // Check IP Blacklist
  if (store.state.isBlocked) {
    alert('দুঃখিত, আপনার আইপি এড্রেসটি সিস্টেম কর্তৃক স্থগিত (Blocked) রয়েছে। অর্ডার করতে আমাদের সাথে সরাসরি যোগাযোগ করুন।');
    return;
  }

  // Set Loading UI
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> অর্ডার প্রসেস হচ্ছে...';
  }

  try {
    const items = store.state.cartItems && store.state.cartItems.length > 0
      ? store.state.cartItems
      : [{ size: '12ft', color: 'light-pink', quantity: 1, price: 690 }];
    const primaryItem = items[0];
    const sizeName = store.state.sizes[primaryItem.size]?.name || primaryItem.size;
    const colorName = store.state.colors[primaryItem.color]?.name || primaryItem.color;
    const totalQty = items.reduce((acc, it) => acc + it.quantity, 0);
    const total = store.grandTotal;
    const deliveryArea = store.state.deliveryCharge === 70 ? 'ঢাকার ভেতরে (৳৭০)' : 'ঢাকার বাইরে (৳১৩০)';

    const eventId = 'SZK-' + Date.now() + '-' + Math.random().toString(36).substring(2, 7);

    const orderPayload: OrderPayload = {
      name,
      phone: cleanPhone,
      email: email || undefined,
      address,
      size: sizeName,
      color: colorName,
      quantity: totalQty,
      items: items,
      has_battery: store.state.hasBattery,
      battery_qty: store.state.hasBattery ? (store.state.batteryQuantity || 1) : 0,
      battery_amount: store.batteryTotal,
      delivery_charge: store.state.deliveryCharge,
      total_amount: total,
      event_id: eventId,
      uuid: store.state.userUuid,
      user_ip: store.state.userIp,
      user_agent: navigator.userAgent,
      device_type: store.state.deviceType,
      os: store.state.userOs,
      fbp: getOrInitFbp(),
      fbc: getOrInitFbc() || undefined
    };

    // Dynamic E-Commerce Product Contents for Meta Pixel, CAPI, GA4 & GTM
    const fbContents: Array<{ id: string; quantity: number; item_price: number }> = items.map(it => ({
      id: `cherry-${it.size}`,
      quantity: Number(it.quantity) || 1,
      item_price: Number(it.price) || (it.size === '6ft' ? 390 : 690)
    }));
    if (store.state.hasBattery) {
      const batQty = store.state.batteryQuantity || 1;
      fbContents.push({
        id: 'addon-battery',
        quantity: Number(batQty),
        item_price: Number(store.state.batteryPrice) || 60
      });
    }
    const totalItemCount = fbContents.reduce((sum, it) => sum + it.quantity, 0);
    const numericTotal = Number(total);

    // 1. Meta Pixel Purchase Event (Safely Isolated)
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Purchase', {
          content_name: 'Artificial Cherry Flower String light',
          content_type: 'product',
          contents: fbContents,
          content_ids: fbContents.map(c => c.id),
          num_items: totalItemCount,
          value: numericTotal,
          currency: 'BDT'
        }, { eventID: eventId });
      }
    } catch (e) {
      console.warn('Meta Pixel tracking error:', e);
    }

    // 2. Meta Conversions API (CAPI) Direct Client Dispatch (Safely Isolated)
    try {
      if (activeAnalyticsConfig?.metaPixelId && activeAnalyticsConfig?.metaCapiToken) {
        const intlPhone = '880' + cleanPhone.replace(/^0+/, '');
        const fbpVal = getOrInitFbp();
        const fbcVal = getOrInitFbc();
        const capiPayload: Record<string, any> = {
          data: [{
            event_name: 'Purchase',
            event_time: Math.floor(Date.now() / 1000),
            event_id: eventId,
            event_source_url: window.location.href,
            action_source: 'website',
            user_data: {
              client_ip_address: store.state.userIp || undefined,
              client_user_agent: navigator.userAgent,
              fbp: fbpVal,
              fbc: fbcVal || undefined,
              external_id: store.state.userUuid,
              ph: [intlPhone, cleanPhone],
              em: email ? [email.trim().toLowerCase()] : undefined,
              fn: [name.trim().split(' ')[0].toLowerCase()],
              country: ['bd'],
              ct: ['dhaka']
            },
            custom_data: {
              currency: 'BDT',
              value: numericTotal,
              content_type: 'product',
              content_name: 'Artificial Cherry Flower String light',
              contents: fbContents,
              content_ids: fbContents.map(c => c.id),
              num_items: totalItemCount
            }
          }]
        };
        if (activeAnalyticsConfig.metaTestEventCode) {
          capiPayload.test_event_code = activeAnalyticsConfig.metaTestEventCode;
        }

        fetch(`https://graph.facebook.com/v18.0/${encodeURIComponent(activeAnalyticsConfig.metaPixelId)}/events?access_token=${encodeURIComponent(activeAnalyticsConfig.metaCapiToken)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(capiPayload)
        }).catch(e => console.warn('Meta CAPI direct dispatch:', e));
      }
    } catch (e) {
      console.warn('CAPI dispatch error:', e);
    }

    // 3. Google Analytics 4 (GA4) Purchase Event (Safely Isolated)
    try {
      if (typeof window.gtag === 'function') {
        const gaItems = items.map(it => ({
          item_name: 'Artificial Cherry Flower String light',
          item_variant: `${it.size} - ${it.color}`,
          price: it.price,
          quantity: it.quantity
        }));
        if (store.state.hasBattery) {
          const batQty = store.state.batteryQuantity || 1;
          gaItems.push({
            item_name: `${batQty * 3}x AA Pencil Batteries (${batQty} Sets)`,
            item_variant: 'Pencil Battery Addon',
            price: store.state.batteryPrice,
            quantity: batQty
          });
        }

        window.gtag('event', 'purchase', {
          transaction_id: eventId,
          value: total,
          currency: 'BDT',
          shipping: store.state.deliveryCharge,
          items: gaItems
        });
      }
    } catch (e) {
      console.warn('GA4 purchase event error:', e);
    }

    // 4. Google Tag Manager (GTM) Purchase Event (Safely Isolated)
    try {
      if (Array.isArray(window.dataLayer)) {
        const gtmItems = items.map(it => ({
          item_name: 'Artificial Cherry Flower String light',
          item_variant: `${it.size} - ${it.color}`,
          price: it.price,
          quantity: it.quantity
        }));
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: eventId,
            value: total,
            currency: 'BDT',
            shipping: store.state.deliveryCharge,
            items: gtmItems
          },
          customer: {
            device: store.state.deviceType,
            os: store.state.userOs,
            ip: store.state.userIp,
            uuid: store.state.userUuid
          }
        });
      }
    } catch (e) {
      console.warn('GTM dataLayer error:', e);
    }

    const invoiceId = 'SZK-' + Date.now().toString().slice(-6);
    const nowDhaka = new Date().toLocaleString('en-US', {
      timeZone: 'Asia/Dhaka',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    const fullOrderRecord = {
      order_id: invoiceId,
      created_at: nowDhaka,
      customer_name: name,
      customer_phone: cleanPhone,
      delivery_address: address,
      delivery_charge: store.state.deliveryCharge,
      total_amount: total,
      size: sizeName,
      color: colorName,
      quantity: totalQty,
      items: items.map(it => ({
        size: store.state.sizes[it.size]?.name || it.size,
        color: store.state.colors[it.color]?.name || it.color,
        quantity: it.quantity,
        price: it.price
      })),
      has_battery: store.state.hasBattery,
      battery_qty: store.state.hasBattery ? (store.state.batteryQuantity || 1) : 0,
      battery_amount: store.batteryTotal,
      device_type: store.state.deviceType,
      user_os: store.state.userOs,
      user_ip: store.state.userIp || 'Visitor IP',
      uuid: store.state.userUuid,
      status: 'Pending',
      steadfast_status: 'অপেক্ষমান (Pending)',
      bizmation_status: activeBizmationConfig?.enabled ? 'Sent' : 'None'
    };

    // 1. Guaranteed Local Order Storage for Instant Admin Visibility
    try {
      const existingOrders = JSON.parse(localStorage.getItem('sazook_admin_orders') || '[]');
      existingOrders.unshift(fullOrderRecord);
      localStorage.setItem('sazook_admin_orders', JSON.stringify(existingOrders));
    } catch (e) {}

    try {
      const customerOrders = JSON.parse(localStorage.getItem('sazook_customer_orders') || '[]');
      customerOrders.unshift(fullOrderRecord);
      localStorage.setItem('sazook_customer_orders', JSON.stringify(customerOrders));
    } catch (e) {}

    // 2. Real-time broadcast to Admin Panel if open in another tab
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const bc = new BroadcastChannel('sazook_orders_channel');
        bc.postMessage({ type: 'NEW_ORDER', order: fullOrderRecord });
        setTimeout(() => bc.close(), 1000);
      }
    } catch (e) {}

    // 3. Dispatch to Bizmation API if configured
    try {
      if (activeBizmationConfig?.enabled && activeBizmationConfig.apiToken) {
        ApiClient.submitBizmationOrder(
          activeBizmationConfig.apiToken,
          activeBizmationConfig.inventoryId,
          activeBizmationConfig.checkDuplicateOrder,
          orderPayload
        ).catch(err => console.warn('Bizmation sync error:', err));
      }
    } catch (e) {}

    let finalOrderId = invoiceId;
    let trackingCode: string | null = null;

    try {
      const data = await ApiClient.submitOrder({ ...orderPayload, ...fullOrderRecord } as any);
      if (data && data.order_id) finalOrderId = data.order_id;
      if (data && data.tracking_code) trackingCode = data.tracking_code;
    } catch (err) {
      console.warn('API sync completed with fallback:', err);
    }

    const trackingNotice = trackingCode 
      ? `<p style="font-size: 13px; color: #2ec4b6; margin-top: 8px;"><strong>Steadfast ট্র্যাকিং কোড:</strong> ${trackingCode}</p>`
      : `<p style="font-size: 12px; color: #2ec4b6; margin-top: 5px;"><i class="fa-solid fa-truck"></i> স্টিডফাস্ট কুরিয়ারের মাধ্যমে পাঠানো হচ্ছে।</p>`;

    const itemsSummaryHtml = items.map((it, idx) => {
      const sName = store.state.sizes[it.size]?.name || it.size;
      const cName = store.state.colors[it.color]?.name || it.color;
      return `<div style="padding: 4px 0;">• আইটেম ${idx + 1}: <strong>${sName}</strong> (${cName}) × ${it.quantity} পিস = ৳${formatBanglaNumber(it.price * it.quantity)}</div>`;
    }).join('');

    const batQty = store.state.batteryQuantity || 1;
    const batteryHtml = store.state.hasBattery 
      ? `<div style="color: #ffb703; padding: 4px 0;">• ব্যাটারি: ${formatBanglaNumber(batQty)} সেট (${formatBanglaNumber(batQty * 3)}টি AA পেন্সিল ব্যাটারি) যোগ করা হয়েছে (+৳${formatBanglaNumber(store.batteryTotal)})</div>` 
      : '';

    const itemsSummaryText = items.map((it, idx) => {
      const sName = store.state.sizes[it.size]?.name || it.size;
      const cName = store.state.colors[it.color]?.name || it.color;
      return `${idx + 1}. ${sName} (${cName}) × ${it.quantity} পিস`;
    }).join('\n');
    const batteryText = store.state.hasBattery 
      ? `\n- ব্যাটারি: ${formatBanglaNumber(batQty)} সেট (${formatBanglaNumber(batQty * 3)}টি AA পেন্সিল ব্যাটারি) (+৳${formatBanglaNumber(store.batteryTotal)})` 
      : '';

    const modalSummary = document.getElementById('modalOrderDetails');
    const waEncodedMsg = encodeURIComponent(`নতুন অর্ডার কনফার্মেশন:\n- ইনভয়েস: ${finalOrderId}\n- নাম: ${name}\n- ফোন: ${cleanPhone}\n- ঠিকানা: ${address}\n- আইটেমসমূহ:\n${itemsSummaryText}${batteryText}\n- ডিভাইস: ${store.state.deviceType} (${store.state.userOs})\n- মোট মূল্য: ৳${total} (ক্যাশ অন ডেলিভারি)`);
    const waConfirmUrl = `https://wa.me/88${store.state.whatsappNumber.replace(/[^0-9]/g, '')}?text=${waEncodedMsg}`;

    if (modalSummary) {
      modalSummary.innerHTML = `
        <p><strong>ইনভয়েস নং:</strong> ${finalOrderId}</p>
        <p><strong>গ্রাহকের নাম:</strong> ${name}</p>
        <p><strong>মোবাইল নম্বর:</strong> ${cleanPhone}</p>
        <p><strong>ডেলিভারি ঠিকানা:</strong> ${address}</p>
        <div style="background: #f8fafc; border: 1px solid #ede9fe; padding: 12px; border-radius: 10px; margin: 10px 0;">
          <strong>অর্ডারকৃত আইটেম:</strong>
          ${itemsSummaryHtml}
          ${batteryHtml}
        </div>
        <p><strong>ডেলিভারি এরিয়া:</strong> ${deliveryArea}</p>
        <hr style="border:0; border-top: 1px solid #e2e8f0; margin: 10px 0;">
        <p style="font-size: 16px; color: #5A189A;"><strong>সর্বমোট প্রদেয়:</strong> ৳${formatBanglaNumber(total)} (ক্যাশ অন ডেলিভারি)</p>
        ${trackingNotice}
        <div style="margin-top: 15px;">
          <a href="${waConfirmUrl}" target="_blank" class="btn-order-wa" style="display: flex; justify-content: center; text-decoration: none;">
            <i class="fa-brands fa-whatsapp"></i> হোয়াটসঅ্যাপেও অর্ডার ডিটেইলস পাঠান
          </a>
        </div>
      `;
    }

    const modal = document.getElementById('orderSuccessModal');
    if (modal) modal.classList.add('active');
    (document.getElementById('checkoutForm') as HTMLFormElement | null)?.reset();

    // Mark as submitted and purge from Abandoned Cart
    isOrderSubmitted = true;
    clearAbandonedCart(store.state.userUuid, cleanPhone);

  } catch (err: any) {
    console.error('Order submission error:', err);
    alert('অর্ডার প্রসেস করার সময় সামান্য সমস্যা হয়েছে। দয়া করে পেজটি রিফ্রেশ করে আবার চেষ্টা করুন বা সরাসরি হোয়াটসঅ্যাপে যোগাযোগ করুন।');
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalBtnText;
    }
  }
}

/**
 * Order Tracking
 */
export async function trackOrderSubmit(): Promise<void> {
  const phoneInput = document.getElementById('trackPhoneInput') as HTMLInputElement | null;
  const resBox = document.getElementById('trackResultBox');
  if (!phoneInput || !resBox) return;

  const phone = phoneInput.value.trim();
  if (!phone || phone.length < 10) {
    resBox.style.display = 'block';
    resBox.innerHTML = '<span style="color:#ff4d6d;">সঠিক মোবাইল নম্বর লিখুন।</span>';
    return;
  }

  resBox.style.display = 'block';
  resBox.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> ট্র্যাক করা হচ্ছে...';

  try {
    const data = await ApiClient.trackOrder(phone);
    if (data.status === 'success' && data.orders && data.orders.length > 0) {
      resBox.innerHTML = data.orders.map(o => `
        <div style="border-bottom: 1px solid #e2e8f0; padding: 10px 0;">
          <p><strong>ইনভয়েস:</strong> ${o.order_id}</p>
          <p><strong>তারিখ:</strong> ${o.created_at}</p>
          <p><strong>আইটেম:</strong> ${o.size} (${o.color}) × ${o.quantity} পিস</p>
          <p><strong>মোট মূল্য:</strong> ৳${formatBanglaNumber(o.total_amount)}</p>
          <p><strong>স্ট্যাটাস:</strong> <span style="color:#2ec4b6;">${o.status}</span></p>
          ${o.tracking_code ? `<p><strong>Steadfast ট্র্যাকিং:</strong> <span style="color:#ff85a1;">${o.tracking_code}</span></p>` : ''}
        </div>
      `).join('');
    } else {
      resBox.innerHTML = '<span style="color:#ffb703;">এই নম্বরে কোনো অর্ডার খুঁজে পাওয়া যায়নি।</span>';
    }
  } catch {
    resBox.innerHTML = '<span style="color:#ff4d6d;">ট্র্যাকিং সার্ভারে সংযোগ পাওয়া যায়নি।</span>';
  }
}

/**
 * Device, UUID & IP Tracking Engine
 */
async function initDeviceTracking(): Promise<void> {
  // 1. UUID
  let uuid = localStorage.getItem('sazook_uuid');
  if (!uuid) {
    uuid = 'szk-' + Math.random().toString(36).substring(2, 10) + '-' + Date.now().toString(36);
    localStorage.setItem('sazook_uuid', uuid);
  }
  store.state.userUuid = uuid;

  // 2. Device & OS Detection
  const ua = navigator.userAgent;
  const isTablet = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/i.test(ua);
  const isMobile = !isTablet && /(mobi|ipod|phone|blackberry|opera mini|fennec|minimo|symbian|psp|nintendo)/i.test(ua);
  store.state.deviceType = isTablet ? 'Tablet' : (isMobile ? 'Mobile' : 'Desktop');

  if (/Windows/i.test(ua)) store.state.userOs = 'Windows';
  else if (/Android/i.test(ua)) store.state.userOs = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) store.state.userOs = 'iOS';
  else if (/Macintosh|Mac OS X/i.test(ua)) store.state.userOs = 'MacOS';
  else if (/Linux/i.test(ua)) store.state.userOs = 'Linux';
  else store.state.userOs = 'Other';

  // 3. IP lookup with timeout
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2500);
    const res = await fetch('https://api.ipify.org?format=json', { signal: controller.signal });
    clearTimeout(timer);
    const data = await res.json();
    if (data?.ip) {
      store.state.userIp = data.ip;
      checkIpBlockStatus();
    }
  } catch {
    // Non-blocking fallback
  }
}

function checkIpBlockStatus(): void {
  if (!store.state.userIp || !Array.isArray(store.state.blockedIps)) return;
  const isBlocked = store.state.blockedIps.some(ip => ip.trim() === store.state.userIp.trim());
  store.state.isBlocked = isBlocked;

  const submitBtn = document.getElementById('submitOrderBtn') as HTMLButtonElement | null;
  const form = document.getElementById('checkoutForm');

  if (isBlocked) {
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.style.opacity = '0.5';
      submitBtn.style.cursor = 'not-allowed';
      submitBtn.title = 'আপনার আইপি এড্রেসটি স্থগিত রয়েছে।';
    }
    let notice = document.getElementById('ipBlockedWarning');
    if (!notice && form) {
      notice = document.createElement('div');
      notice.id = 'ipBlockedWarning';
      notice.style.cssText = 'color:#ff4d6d; font-size:14px; font-weight:700; background:rgba(255,77,109,0.12); border:1px solid #ff4d6d; padding:12px 16px; border-radius:10px; margin-top:15px; text-align:center;';
      notice.innerHTML = `<i class="fa-solid fa-ban"></i> আপনার আইপি (${store.state.userIp}) স্থগিত রয়েছে। অর্ডার করতে আমাদের হটলাইনে কল করুন।`;
      form.appendChild(notice);
    }
  }
}

// ----------------------------------------------------
// Meta Cookie Helpers for 10/10 Event Match Quality
// ----------------------------------------------------
export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
  return match ? decodeURIComponent(match[3]) : null;
}

export function setCookie(name: string, value: string, days = 90): void {
  const d = new Date();
  d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

export function getOrInitFbp(): string {
  let fbp = getCookie('_fbp');
  if (!fbp) {
    fbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 10000000000)}`;
    setCookie('_fbp', fbp, 90);
  }
  return fbp;
}

export function getOrInitFbc(): string | null {
  let fbc = getCookie('_fbc');
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid');
  if (fbclid) {
    fbc = `fb.1.${Date.now()}.${fbclid}`;
    setCookie('_fbc', fbc, 90);
  }
  return fbc;
}

/**
 * Analytics Engine: Meta Pixel, CAPI, GA4, GTM, Search Console & Domain Verification
 */
function initAnalytics(cfg: AnalyticsConfig): void {
  // Auto-init fbp and fbc
  getOrInitFbp();
  getOrInitFbc();

  // 1. Meta Pixel Initialization & PageView
  if (cfg.metaPixelId && !document.getElementById('meta-pixel-script')) {
    const pxScript = document.createElement('script');
    pxScript.id = 'meta-pixel-script';
    pxScript.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');`;
    document.head.appendChild(pxScript);

    if (typeof window.fbq !== 'undefined') {
      window.fbq('init', cfg.metaPixelId);
      if (cfg.metaTestEventCode) {
        window.fbq('set', 'testEventCode', cfg.metaTestEventCode);
      }
      window.fbq('track', 'PageView');
    }
  }

  // 2. Google Analytics 4 (GA4)
  if (cfg.googleAnalyticsId && !document.getElementById('ga4-script')) {
    const gaScript = document.createElement('script');
    gaScript.id = 'ga4-script';
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(cfg.googleAnalyticsId)}`;
    document.head.appendChild(gaScript);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function(){ (window.dataLayer as any).push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', cfg.googleAnalyticsId);
    window.gtag('event', 'page_view');
  }

  // 3. Google Tag Manager (GTM)
  if (cfg.googleTagManagerId && !document.getElementById('gtm-script')) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const gtmScript = document.createElement('script');
    gtmScript.id = 'gtm-script';
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(cfg.googleTagManagerId)}`;
    document.head.appendChild(gtmScript);
  }

  // 4. Google Search Console Verification Meta Tag
  if (cfg.googleSearchConsole && !document.querySelector('meta[name="google-site-verification"]')) {
    const meta = document.createElement('meta');
    meta.name = 'google-site-verification';
    meta.content = cfg.googleSearchConsole.replace(/<meta.*content="|">|\/>/gi, '').trim();
    document.head.appendChild(meta);
  }

  // 5. Facebook Domain Verification Meta Tag
  if (cfg.facebookDomainVerification && !document.querySelector('meta[name="facebook-domain-verification"]')) {
    const meta = document.createElement('meta');
    meta.name = 'facebook-domain-verification';
    meta.content = cfg.facebookDomainVerification.replace(/<meta.*content="|">|\/>/gi, '').trim();
    document.head.appendChild(meta);
  }
}

/**
 * Event Tracking Dispatchers for Pixel, GA4, and GTM
 */
export function trackEvent(eventName: string, data: Record<string, any> = {}): void {
  // Meta Pixel
  if (typeof window.fbq !== 'undefined') {
    if (['PageView', 'ViewContent', 'InitiateCheckout', 'Purchase', 'Contact', 'Lead'].includes(eventName)) {
      window.fbq('track', eventName, data);
    } else {
      window.fbq('trackCustom', eventName, data);
    }
  }

  // GA4
  if (typeof window.gtag !== 'undefined') {
    const gaEventMap: Record<string, string> = {
      'ViewContent': 'view_item',
      'InitiateCheckout': 'begin_checkout',
      'Purchase': 'purchase',
      'Contact': 'contact_whatsapp',
      'Lead': 'lead_call'
    };
    const mapped = gaEventMap[eventName] || eventName.toLowerCase();
    window.gtag('event', mapped, data);
  }

  // GTM dataLayer
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: eventName.toLowerCase(),
      ...data,
      timestamp: Date.now()
    });
  }
}

function initTrackingListeners(): void {
  // Form focus -> InitiateCheckout / begin_checkout
  const form = document.getElementById('checkoutForm');
  if (form) {
    let initiated = false;
    form.addEventListener('focusin', () => {
      if (!initiated) {
        initiated = true;
        const icContents = store.state.cartItems.map(it => ({
          id: `cherry-${it.size}`,
          quantity: Number(it.quantity) || 1,
          item_price: Number(it.price) || (it.size === '6ft' ? 390 : 690)
        }));
        trackEvent('InitiateCheckout', {
          content_name: 'Artificial Cherry Flower String light',
          content_category: 'Home Decor',
          content_type: 'product',
          contents: icContents,
          content_ids: icContents.map(c => c.id),
          num_items: icContents.reduce((s, c) => s + c.quantity, 0),
          value: Number(store.grandTotal) || Number(store.currentSize.price),
          currency: 'BDT'
        });
      }
    });
  }

  // WhatsApp click -> Contact event
  document.querySelectorAll('a[href*="wa.me"], #heroWhatsappBtn, #formWhatsappBtn, .btn-wa, .sticky-btn-wa').forEach(el => {
    el.addEventListener('click', () => {
      trackEvent('Contact', {
        channel: 'WhatsApp',
        content_name: 'WhatsApp Order Inquiry'
      });
    });
  });

  // Phone Call click -> Lead event
  document.querySelectorAll('a[href^="tel:"], #headerCallBtn').forEach(el => {
    el.addEventListener('click', () => {
      trackEvent('Lead', {
        channel: 'Phone Call',
        content_name: 'Hotline Call Click'
      });
    });
  });
}

/**
 * Real-Time Abandoned Cart Tracker
 * - Captures customer Name, Phone, Address as they type
 * - Excludes or purges automatically when order is submitted
 */
function initAbandonedCartTracker(): void {
  const form = document.getElementById('checkoutForm');
  if (!form) return;

  const fieldIds = ['custName', 'custPhone', 'custEmail', 'custAddress'];
  fieldIds.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('input', () => scheduleAbandonedCartSync());
      el.addEventListener('change', () => scheduleAbandonedCartSync(true));
      el.addEventListener('blur', () => scheduleAbandonedCartSync(true));
    }
  });

  const qtyInput = document.getElementById('orderQty');
  if (qtyInput) {
    qtyInput.addEventListener('change', () => scheduleAbandonedCartSync());
  }

  // Auto-sync when customer closes tab or navigates away
  window.addEventListener('beforeunload', () => {
    if (!isOrderSubmitted) syncAbandonedCartNow(true);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && !isOrderSubmitted) {
      syncAbandonedCartNow(true);
    }
  });
}

function scheduleAbandonedCartSync(immediate = false): void {
  if (isOrderSubmitted) return;
  if (abandonedSyncTimer) clearTimeout(abandonedSyncTimer);

  if (immediate) {
    syncAbandonedCartNow();
  } else {
    abandonedSyncTimer = setTimeout(() => {
      syncAbandonedCartNow();
    }, 1200);
  }
}

function syncAbandonedCartNow(isBeacon = false): void {
  if (isOrderSubmitted) return;

  const nameInput = document.getElementById('custName') as HTMLInputElement | null;
  const phoneInput = document.getElementById('custPhone') as HTMLInputElement | null;
  const emailInput = document.getElementById('custEmail') as HTMLInputElement | null;
  const addressInput = document.getElementById('custAddress') as HTMLTextAreaElement | null;

  const name = nameInput?.value.trim() || '';
  const rawPhone = phoneInput?.value.trim() || '';
  const phone = sanitizePhoneNumber(rawPhone);
  const email = emailInput?.value.trim() || '';
  const address = addressInput?.value.trim() || '';

  // Only sync if customer has entered at least a name, phone, email, or address
  if (!name && !phone && !email && !address) return;

  const items = store.state.cartItems;
  const primaryItem = items[0] || { size: '12ft', color: 'light-pink', quantity: 1, price: 1290 };
  const sizeName = store.state.sizes[primaryItem.size]?.name || primaryItem.size;
  const colorName = store.state.colors[primaryItem.color]?.name || primaryItem.color;
  const totalQty = items.reduce((acc, it) => acc + it.quantity, 0);

  const payload: AbandonedCartRecord = {
    name,
    phone,
    email: email || undefined,
    address,
    size: sizeName,
    color: colorName,
    quantity: totalQty,
    items: items,
    has_battery: store.state.hasBattery,
    battery_qty: store.state.hasBattery ? (store.state.batteryQuantity || 1) : 0,
    total_amount: store.grandTotal,
    uuid: store.state.userUuid,
    user_ip: store.state.userIp,
    device_type: store.state.deviceType,
    user_os: store.state.userOs,
    updated_at: new Date().toISOString()
  };

  try {
    localStorage.setItem('sazook_abandoned_cart', JSON.stringify(payload));
  } catch {}

  const jsonStr = JSON.stringify(payload);

  if (isBeacon && navigator.sendBeacon) {
    const blob = new Blob([jsonStr], { type: 'application/json' });
    navigator.sendBeacon('api/abandoned-cart.php', blob);
    return;
  }

  fetch('api/abandoned-cart.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: jsonStr,
    keepalive: true
  }).catch(() => {
    fetch('http://localhost:3000/api/abandoned-cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: jsonStr
    }).catch(() => {});
  });
}

function clearAbandonedCart(uuid: string, phone: string): void {
  isOrderSubmitted = true;
  try {
    localStorage.removeItem('sazook_abandoned_cart');
  } catch {}

  const payload = JSON.stringify({
    action: 'clear',
    uuid,
    phone
  });

  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon('api/abandoned-cart.php', blob);
  }

  fetch('api/abandoned-cart.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true
  }).catch(() => {
    fetch('http://localhost:3000/api/abandoned-cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload
    }).catch(() => {});
  });
}

// ==========================================
// Customer Reviews System (Image + Ratings, No Date)
// ==========================================
let activeReviews: ReviewItem[] = [
  {
    id: 'rev_1790097926_580',
    name: 'Tamima Tanvi',
    rating: 5,
    location: 'Verified Buyer',
    comment: 'চেরী ব্লোসম  ফুল 🌸 টা সেট করার পরে মিরর টা অনেক সুন্দর লাগতেছিলো,, ফুল 🌸টাকে একেবারে জীবন্ত মনে হচ্ছে ☺️',
    image: 'assets/images/uploads/rev_1790097926_942aa4.jpg'
  },
  {
    id: 'rev_1790097875_836',
    name: 'Shumaiya Afren',
    rating: 5,
    location: 'Verified Buyer',
    comment: 'যেমনটা আশা করছিলাম ঠিক তেমনই❤️ 💜💜💜❤️',
    image: 'assets/images/uploads/rev_1790097875_bfcc52.jpg'
  },
  {
    id: 'rev_1790097815_179',
    name: 'Anonymous participant',
    rating: 5,
    location: 'Verified Buyer',
    comment: 'So beautiful lighting apnara sobay nity parn 🥰🥰🥰🥰',
    image: 'assets/images/uploads/rev_1790097815_49a192.jpg'
  },
  {
    id: 'rev_1790097595_635',
    name: 'তানজিলা আক্তার',
    rating: 5,
    location: 'Verified Buyer',
    comment: 'পীচ কালারটি আমার রুমের পেস্টাল কালার থিমের সাথে দারুণভাবে ম্যাচ করেছে। ব্যাটারি সিস্টেম হওয়ায় কোনো প্লাগ পয়েন্টের উপর নির্ভর করতে হয় না। দেখতেও খুব গর্জিয়াস!',
    image: 'assets/images/uploads/rev_1790097595_271b9b.jpg'
  },
  {
    id: 'rev_1789998623213',
    name: 'Mubashera Sumi',
    rating: 5,
    location: '(Verified Buyer)',
    comment: 'So beautiful🌸',
    image: 'assets/images/uploads/rev_1790055317_be0007.jpg'
  },
  {
    id: 'rev_1789998623214',
    name: 'Shafia Akhter',
    rating: 5,
    location: 'ধানমন্ডি, ঢাকা (Verified Buyer)',
    comment: 'Thank you Sazook for the beautiful flowers with lighting 💓',
    image: 'assets/images/uploads/rev_1790055231_96f765.jpg'
  }
];
let currentRatingSelection: number = 5;
let selectedReviewImageBase64: string = '';
let currentReviewIndex: number = 0;

export async function initReviews(fallbackItems?: ReviewItem[]): Promise<void> {
  let loadedList: ReviewItem[] = [];
  const cacheBuster = '?v=' + Date.now();
  const endpoints = ['data/reviews.json' + cacheBuster, 'api/reviews.php' + cacheBuster, '/api/reviews' + cacheBuster];
  for (const ep of endpoints) {
    try {
      const res = await fetch(ep);
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : (Array.isArray(data.reviews) ? data.reviews : []);
        if (list.length > 0) {
          loadedList = list;
          break;
        }
      }
    } catch {}
  }

  // Deduplicate and merge user submitted reviews from local storage
  try {
    const local = localStorage.getItem('sazook_user_reviews');
    if (local) {
      const localReviews: ReviewItem[] = JSON.parse(local);
      if (Array.isArray(localReviews)) {
        const existingNames = new Set(loadedList.map(r => (r.name || '').trim().toLowerCase()));
        const validLocal: ReviewItem[] = [];
        localReviews.forEach(lr => {
          const normName = (lr.name || '').trim().toLowerCase();
          if (normName && !existingNames.has(normName) && lr.comment) {
            validLocal.push(lr);
            loadedList.unshift(lr);
            existingNames.add(normName);
          }
        });
        if (validLocal.length !== localReviews.length) {
          localStorage.setItem('sazook_user_reviews', JSON.stringify(validLocal));
        }
      }
    }
  } catch {}

  if (loadedList.length > 0) {
    activeReviews = loadedList;
    renderReviews(activeReviews);
    initReviewTouchGestures();
    startReviewAutoSlide();
    return;
  }

  // Fallback to config items
  if (fallbackItems && fallbackItems.length > 0) {
    activeReviews = fallbackItems.map((it, idx) => ({
      ...it,
      rating: it.rating || 5,
      image: it.image || (idx === 0 ? 'assets/images/12ft-light-pink.jpg' : (idx === 1 ? 'assets/images/cherry-deep-pink.jpg' : 'assets/images/12ft-purple.jpg'))
    }));
    renderReviews(activeReviews);
    initReviewTouchGestures();
    startReviewAutoSlide();
  }
}

let touchStartX = 0;
let touchEndX = 0;
let reviewTouchBound = false;
let reviewAutoSlideTimer: any = null;

export function startReviewAutoSlide(): void {
  stopReviewAutoSlide();
  reviewAutoSlideTimer = setInterval(() => {
    navigateReviews(1, false);
  }, 3500);
}

export function stopReviewAutoSlide(): void {
  if (reviewAutoSlideTimer) {
    clearInterval(reviewAutoSlideTimer);
    reviewAutoSlideTimer = null;
  }
}

function initReviewTouchGestures(): void {
  if (reviewTouchBound) return;
  const wrapper = document.getElementById('reviewsCarouselWrapper');
  const viewport = document.getElementById('reviewsViewport') || wrapper;
  if (!viewport || !wrapper) return;

  // Hover Pause & Resume
  wrapper.addEventListener('mouseenter', () => stopReviewAutoSlide());
  wrapper.addEventListener('mouseleave', () => startReviewAutoSlide());

  // Touch Gestures
  viewport.addEventListener('touchstart', (e: TouchEvent) => {
    stopReviewAutoSlide();
    if (e.touches && e.touches.length > 0) {
      touchStartX = e.touches[0].screenX;
    }
  }, { passive: true });

  viewport.addEventListener('touchend', (e: TouchEvent) => {
    if (e.changedTouches && e.changedTouches.length > 0) {
      touchEndX = e.changedTouches[0].screenX;
      const diff = touchEndX - touchStartX;
      if (Math.abs(diff) > 40) {
        if (diff < 0) {
          navigateReviews(1, true);
        } else {
          navigateReviews(-1, true);
        }
      }
    }
    startReviewAutoSlide();
  }, { passive: true });

  reviewTouchBound = true;
}

export function updateReviewsCarousel(): void {
  const track = document.getElementById('reviewsGrid') as HTMLElement | null;
  const dotsContainer = document.getElementById('reviewsDots');
  if (!track || !activeReviews || activeReviews.length === 0) return;

  const totalCards = activeReviews.length;
  const isMobile = window.innerWidth <= 768;
  const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
  const cardsPerView = isMobile ? 1 : (isTablet ? 2 : 3);
  const maxIndex = Math.max(0, totalCards - cardsPerView);

  if (currentReviewIndex > maxIndex) {
    currentReviewIndex = 0;
  }
  if (currentReviewIndex < 0) {
    currentReviewIndex = maxIndex;
  }

  const firstCard = track.children[0] as HTMLElement | undefined;
  if (firstCard) {
    const cardWidth = firstCard.offsetWidth || firstCard.getBoundingClientRect().width || (isMobile ? Math.max(260, window.innerWidth - 116) : 345);
    const gap = isMobile ? 14 : 20;
    const offset = currentReviewIndex * (cardWidth + gap);
    track.style.transform = `translateX(-${offset}px)`;
  }

  // Render Dots
  if (dotsContainer) {
    const totalPages = Math.max(1, maxIndex + 1);
    dotsContainer.innerHTML = Array.from({ length: totalPages }, (_, i) => `
      <button type="button" class="review-dot ${i === currentReviewIndex ? 'active' : ''}" onclick="goToReviewPage(${i})" aria-label="Review ${i + 1}"></button>
    `).join('');
  }
}

export function navigateReviews(direction: number, isUserAction = true): void {
  const totalCards = activeReviews?.length || 6;
  const isMobile = window.innerWidth <= 768;
  const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
  const cardsPerView = isMobile ? 1 : (isTablet ? 2 : 3);
  const maxIndex = Math.max(0, totalCards - cardsPerView);

  currentReviewIndex += direction;
  if (currentReviewIndex > maxIndex) {
    currentReviewIndex = 0;
  } else if (currentReviewIndex < 0) {
    currentReviewIndex = maxIndex;
  }

  updateReviewsCarousel();

  if (isUserAction) {
    startReviewAutoSlide();
  }
}

export function goToReviewPage(pageIndex: number): void {
  currentReviewIndex = pageIndex;
  updateReviewsCarousel();
  startReviewAutoSlide();
}

export function renderReviews(reviews: ReviewItem[]): void {
  const container = document.getElementById('reviewsGrid');
  if (!container) return;

  container.innerHTML = reviews.map((rev, index) => {
    const rating = rev.rating || 5;
    const starsHtml = '<i class="fa-solid fa-star"></i>'.repeat(Math.min(5, Math.max(1, rating)));
    const imageHtml = rev.image ? `
      <div class="review-photo-wrap" onclick="openReviewImageModal('${escapeHtmlAttr(rev.image)}')">
        <img src="${escapeHtmlAttr(rev.image)}" alt="Review photo by ${escapeHtml(rev.name)}" class="review-customer-img" loading="lazy" onerror="this.parentElement.style.display='none'">
        <span class="view-photo-badge"><i class="fa-solid fa-magnifying-glass-plus"></i> বড় ছবি দেখুন</span>
      </div>
    ` : '';

    return `
      <div class="review-card" data-review-id="${rev.id || index}">
        <div>
          <div class="stars">${starsHtml}</div>
          <p class="review-text">"${escapeHtml(rev.comment)}"</p>
        </div>
        ${imageHtml}
        <div class="reviewer">
          <i class="fa-solid fa-circle-check reviewer-badge-icon"></i>
          <strong>${escapeHtml(rev.name)}</strong> - <small>${escapeHtml(rev.location || 'Verified Buyer')}</small>
        </div>
      </div>
    `;
  }).join('');

  setTimeout(() => updateReviewsCarousel(), 60);
}

function escapeHtml(str: string): string {
  if (!str) return '';
  return str.replace(/[&<>"']/g, (m) => {
    switch (m) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return m;
    }
  });
}

function escapeHtmlAttr(str: string): string {
  if (!str) return '';
  return str.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function openReviewModal(): void {
  const overlay = document.getElementById('reviewModalOverlay');
  if (!overlay) return;
  setReviewRating(5);
  const statusEl = document.getElementById('reviewFormStatus');
  if (statusEl) {
    statusEl.style.display = 'none';
    statusEl.className = 'review-form-status';
    statusEl.textContent = '';
  }
  removeReviewImagePreview();
  const form = document.getElementById('customerReviewForm') as HTMLFormElement | null;
  if (form) form.reset();
  overlay.classList.add('active');
}

export function closeReviewModal(): void {
  const overlay = document.getElementById('reviewModalOverlay');
  if (!overlay) return;
  overlay.classList.remove('active');
}

export function closeReviewModalOnBackdrop(e: Event): void {
  if (e.target === document.getElementById('reviewModalOverlay')) {
    closeReviewModal();
  }
}

export function setReviewRating(rating: number): void {
  currentRatingSelection = rating;
  const picker = document.getElementById('starRatingPicker');
  if (!picker) return;
  const btns = picker.querySelectorAll('.star-btn');
  btns.forEach((btn, idx) => {
    if (idx < rating) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });
  const label = document.getElementById('ratingLabelText');
  if (label) {
    const texts = ['', '১/৫ (খারাপ)', '২/৫ (চলনসই)', '৩/৫ (মোটামুটি)', '৪/৫ (ভালো)', '৫/৫ (অসাধারণ!)'];
    label.textContent = texts[rating] || `${rating}/৫`;
  }
}

export function compressReviewImage(file: File, maxWidth = 1000, quality = 0.82): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } else {
          resolve((e.target?.result as string) || '');
        }
      };
      img.onerror = () => resolve((e.target?.result as string) || '');
      img.src = (e.target?.result as string) || '';
    };
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });
}

export async function handleReviewImageSelect(input: HTMLInputElement): Promise<void> {
  if (!input.files || !input.files[0]) return;
  const file = input.files[0];
  if (file.size > 20 * 1024 * 1024) {
    alert('ছবির সাইজ সর্বোচ্চ ২০MB হতে পারবে।');
    input.value = '';
    return;
  }
  
  const previewWrap = document.getElementById('reviewImagePreviewWrap');
  const previewImg = document.getElementById('reviewImagePreview') as HTMLImageElement | null;
  const statusEl = document.getElementById('reviewFormStatus');
  if (statusEl) {
    statusEl.className = 'review-form-status';
    statusEl.textContent = 'ছবি প্রসেস হচ্ছে...';
    statusEl.style.display = 'block';
  }

  try {
    const compressedDataUrl = await compressReviewImage(file, 1000, 0.82);
    selectedReviewImageBase64 = compressedDataUrl || '';
    if (previewWrap && previewImg && compressedDataUrl) {
      previewImg.src = compressedDataUrl;
      previewWrap.style.display = 'inline-block';
    }
  } catch (err) {
    console.error('Image compression error:', err);
  } finally {
    if (statusEl) statusEl.style.display = 'none';
  }
}

export function removeReviewImagePreview(): void {
  selectedReviewImageBase64 = '';
  const input = document.getElementById('reviewFileInput') as HTMLInputElement | null;
  if (input) input.value = '';
  const previewWrap = document.getElementById('reviewImagePreviewWrap');
  if (previewWrap) previewWrap.style.display = 'none';
}

export async function handleReviewSubmit(e: Event): Promise<void> {
  e.preventDefault();
  const nameInput = document.getElementById('reviewAuthorName') as HTMLInputElement | null;
  const locInput = document.getElementById('reviewAuthorLoc') as HTMLInputElement | null;
  const commentInput = document.getElementById('reviewAuthorComment') as HTMLTextAreaElement | null;
  const submitBtn = document.getElementById('btnSubmitReview') as HTMLButtonElement | null;
  const statusEl = document.getElementById('reviewFormStatus');

  const name = (nameInput?.value || '').trim();
  const location = (locInput?.value || '').trim() || 'Verified Buyer';
  const comment = (commentInput?.value || '').trim();

  if (!name || !comment) {
    if (statusEl) {
      statusEl.className = 'review-form-status error';
      statusEl.textContent = 'অনুগ্রহ করে নাম এবং আপনার রিভিউ লিখুন।';
      statusEl.style.display = 'block';
    }
    return;
  }

  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> জমা হচ্ছে...';
  }
  if (statusEl) statusEl.style.display = 'none';

  const payload = {
    name,
    location,
    comment,
    rating: currentRatingSelection,
    image_base64: selectedReviewImageBase64
  };

  const newRev: ReviewItem = {
    id: 'rev_' + Date.now(),
    name,
    location,
    comment,
    rating: currentRatingSelection,
    image: selectedReviewImageBase64 || null
  };

  // Persist locally in browser safely
  try {
    const existing = JSON.parse(localStorage.getItem('sazook_user_reviews') || '[]');
    const safeLocalRev = { ...newRev };
    // Keep local stored image light to avoid localStorage quota crash
    if (safeLocalRev.image && safeLocalRev.image.startsWith('data:') && safeLocalRev.image.length > 300000) {
      safeLocalRev.image = safeLocalRev.image.substring(0, 100000);
    }
    existing.unshift(safeLocalRev);
    if (existing.length > 15) existing.length = 15;
    localStorage.setItem('sazook_user_reviews', JSON.stringify(existing));
  } catch (err) {
    console.warn('localStorage error for reviews:', err);
  }

  // Display immediately in UI
  activeReviews.unshift(newRev);
  renderReviews(activeReviews);

  // Background server sync
  try {
    fetch('api/reviews.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(r => r.json()).then(res => {
      if (res.status === 'success' && res.review && res.review.image) {
        // Update local object with server image URL
        newRev.image = res.review.image;
        renderReviews(activeReviews);
      }
    }).catch(() => {
      fetch('api/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => {});
    });
  } catch {}

  if (statusEl) {
    statusEl.className = 'review-form-status success';
    statusEl.textContent = 'ধন্যবাদ! আপনার ছবি সহ রিভিউটি সফলভাবে প্রকাশিত হয়েছে।';
    statusEl.style.display = 'block';
  }

  if (submitBtn) {
    submitBtn.disabled = false;
    submitBtn.innerHTML = '<i class="fa-solid fa-paper-plane"></i> রিভিউ জমা দিন';
  }

  setTimeout(() => {
    closeReviewModal();
    const revSec = document.getElementById('reviewsSection');
    if (revSec) revSec.scrollIntoView({ behavior: 'smooth' });
  }, 1000);
}

export function openReviewImageModal(imageUrl: string): void {
  const overlay = document.getElementById('lightboxModalOverlay');
  const img = document.getElementById('lightboxImg') as HTMLImageElement | null;
  if (!overlay || !img || !imageUrl) return;
  img.src = imageUrl;
  overlay.classList.add('active');
}

export function closeLightboxModal(): void {
  const overlay = document.getElementById('lightboxModalOverlay');
  if (overlay) overlay.classList.remove('active');
}

export function renderFaqs(faqs: Array<{ q: string; a: string }>): void {
  const container = document.getElementById('faqAccordionContainer') || document.querySelector('.faq-accordion');
  if (!container) return;
  if (!Array.isArray(faqs) || faqs.length === 0) return;

  container.innerHTML = faqs.map((faq, index) => {
    const q = faq.q || '';
    const a = faq.a || '';
    const isOpen = index === 0 ? 'open' : '';
    return `
      <details class="faq-item" ${isOpen}>
        <summary class="faq-question">
          <span id="faqQ${index}">${escapeHtml(q)}</span>
          <i class="fa-solid fa-chevron-down"></i>
        </summary>
        <div class="faq-answer">
          <p id="faqA${index}">${escapeHtml(a)}</p>
        </div>
      </details>
    `;
  }).join('');
}

function exposeGlobalHelpers(): void {
  window.selectSize = selectSize;
  window.syncSizeFromDropdown = selectSize;
  window.selectColorFromGallery = (colorKey: string) => {
    setColor(colorKey);
    if (store.state.cartItems && store.state.cartItems.length > 0) {
      store.state.cartItems[0].color = colorKey;
      renderOrderItems();
      calculateTotals();
    }
    document.getElementById('orderSection')?.scrollIntoView({ behavior: 'smooth' });
  };
  window.syncColorFromDropdown = setColor;
  window.changeQuantity = changeQuantity;
  window.updateDeliveryArea = updateDeliveryArea;
  window.handleFormSubmit = handleFormSubmit;
  window.closeOrderModal = () => document.getElementById('orderSuccessModal')?.classList.remove('active');
  window.openTrackModal = () => {
    document.getElementById('trackOrderModal')?.classList.add('active');
    const box = document.getElementById('trackResultBox');
    if (box) box.style.display = 'none';
  };
  window.closeTrackModal = () => document.getElementById('trackOrderModal')?.classList.remove('active');
  window.trackOrderSubmit = trackOrderSubmit;
  window.addOrderItem = addOrderItem;
  window.removeOrderItem = removeOrderItem;
  window.updateOrderItem = updateOrderItem;
  window.applyCombo = applyCombo;
  window.toggleBatteryAddon = toggleBatteryAddon;
  window.changeBatteryQuantity = changeBatteryQuantity;
  window.setBatteryQuantity = setBatteryQuantity;
  window.updateBatteryUI = updateBatteryUI;
  window.renderGalleryCarousel = renderGalleryCarousel;
  window.openReviewModal = openReviewModal;
  window.closeReviewModal = closeReviewModal;
  window.closeReviewModalOnBackdrop = closeReviewModalOnBackdrop;
  window.setReviewRating = setReviewRating;
  window.handleReviewImageSelect = handleReviewImageSelect;
  window.removeReviewImagePreview = removeReviewImagePreview;
  window.handleReviewSubmit = handleReviewSubmit;
  window.openReviewImageModal = openReviewImageModal;
  window.closeLightboxModal = closeLightboxModal;
  window.navigateReviews = navigateReviews;
  window.goToReviewPage = goToReviewPage;
  window.updateReviewsCarousel = updateReviewsCarousel;
}
